import React, { useState, useEffect, useCallback } from 'react';
import { useUserWithDbId } from '@/modules/auth/hooks/useUserWithDbId';
import { RequestCard } from './RequestCard';
import { useOptimisticVotes } from '../hooks/useOptimisticVotes';
import { api } from '../api';
import * as Sentry from '@sentry/browser';

export function RequestsList({ 
  sortBy, 
  searchQuery, 
  filterBy = 'all', 
  refreshTrigger, 
  onViewComments, 
  onSignInNeeded, 
  onViewDetails,
  onRequestsLoaded
}) {
  const { user, loading: authLoading } = useUserWithDbId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const {
    requests,
    updateRequests,
    toggleOptimisticVote,
    clearPending,
    isPending
  } = useOptimisticVotes([]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentUser = user; // Capture current user reference
      const isAuthenticated = !!currentUser;
      
      console.log('RequestsList: Starting request fetch', { 
        authenticated: isAuthenticated, 
        sortBy, 
        searchQuery, 
        filterBy,
        userEmail: currentUser?.email 
      });
      
      let data;
      
      // Use different API endpoints based on filter
      if (filterBy === 'my-requests' && isAuthenticated) {
        console.log('RequestsList: Fetching user\'s own requests');
        data = await api.getMyRequests();
      } else if (filterBy === 'liked' && isAuthenticated) {
        console.log('RequestsList: Fetching user\'s voted requests');
        data = await api.getSupportedRequests();
      } else if (isAuthenticated) {
        console.log('RequestsList: Fetching all requests (authenticated)');
        data = await api.getRequests(sortBy, searchQuery);
      } else {
        console.log('RequestsList: Fetching public requests (unauthenticated)');
        data = await api.getPublicRequests(sortBy, searchQuery);
      }
      
      console.log('RequestsList: Raw API response:', data);
      
      // Validate that we have an array
      if (!Array.isArray(data)) {
        console.error('RequestsList: API returned non-array data:', data);
        throw new Error('Invalid data format received from API');
      }
      
      // Validate each request has required fields
      const validatedData = data.filter(request => {
        if (!request || !request.id) {
          console.warn('RequestsList: Filtering out invalid request:', request);
          return false;
        }
        return true;
      });
      
      console.log('RequestsList: Validated requests count:', validatedData.length);
      
      // Apply client-side sorting for filtered results if needed
      if ((filterBy === 'my-requests' || filterBy === 'liked') && sortBy !== 'newest') {
        if (sortBy === 'most_committed') {
          validatedData.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
        } else if (sortBy === 'trending') {
          // Simple trending algorithm based on recent activity and vote count
          validatedData.sort((a, b) => {
            const aScore = (a.voteCount || 0) * 0.7 + (a.commentCount || 0) * 0.3;
            const bScore = (b.voteCount || 0) * 0.7 + (b.commentCount || 0) * 0.3;
            return bScore - aScore;
          });
        }
      }
      
      // Apply client-side search for filtered results if needed
      if ((filterBy === 'my-requests' || filterBy === 'liked') && searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchFiltered = validatedData.filter(request => 
          request.title?.toLowerCase().includes(query) ||
          request.description?.toLowerCase().includes(query)
        );
        console.log('RequestsList: Applied client-side search, filtered to:', searchFiltered.length);
        updateRequests(searchFiltered);
      } else {
        console.log('RequestsList: Updating requests state with:', validatedData.length, 'items');
        updateRequests(validatedData);
      }
      
      console.log('RequestsList: Successfully completed request fetch');
    } catch (error) {
      console.error('RequestsList: Error fetching requests:', error);
      setError(error.message);
      Sentry.captureException(error, {
        extra: {
          sortBy,
          searchQuery,
          filterBy,
          isAuthenticated: !!user,
          userEmail: user?.email,
          stackTrace: error.stack
        }
      });
    } finally {
      setLoading(false);
      
      // Notify parent that loading is complete (including error states)
      // Use a longer timeout to ensure DOM updates are complete
      if (onRequestsLoaded) {
        setTimeout(() => onRequestsLoaded(), 100);
      }
    }
  };

  const handleViewDetails = useCallback((request) => {
    if (!request || !request.id) {
      console.error('RequestsList: Invalid request object for view details:', request);
      return;
    }
    
    console.log('RequestsList: Opening detailed view for request:', request.id, request.title);
    
    // Track request detail view
    import('/src/services/analytics/index.js').then(({ trackViewRequestDetails }) => {
      trackViewRequestDetails(request.id);
    });
    
    if (onViewDetails) {
      onViewDetails(request);
    } else {
      console.warn('RequestsList: No onViewDetails handler provided');
    }
  }, [onViewDetails]);

  const handleVoteToggle = useCallback(async (requestId) => {
    if (!user) {
      console.log('RequestsList: User not authenticated for vote toggle');
      if (onSignInNeeded) {
        onSignInNeeded();
      }
      return;
    }

    if (isPending(requestId)) {
      console.log('RequestsList: Vote already pending for request:', requestId);
      return;
    }

    try {
      console.log('RequestsList: Starting vote toggle for request:', requestId);
      
      // Update optimistically
      toggleOptimisticVote(requestId);
      
      // Make API call
      const result = await api.toggleCommitment(requestId);
      console.log('RequestsList: Vote toggle API response:', result);
      
      // Track the vote action
      const { trackVoteRequest } = await import('/src/services/analytics/index.js');
      trackVoteRequest(requestId, result.committed ? 'up' : 'down');
      
      // Clear pending state
      clearPending(requestId);
      
      console.log('RequestsList: Vote toggle successful for request:', requestId);
    } catch (error) {
      console.error('RequestsList: Error toggling vote:', error);
      
      // Clear pending and refresh to get correct state
      clearPending(requestId);
      // Trigger a refresh by updating the refresh trigger instead of calling fetchRequests directly
      
      Sentry.captureException(error, {
        extra: {
          requestId,
          userEmail: user?.email,
          errorMessage: error.message,
          stackTrace: error.stack
        }
      });
      
      // Show user-friendly error
      if (error.message && error.message.includes('Request creators cannot remove their interest')) {
        alert('You cannot remove your vote from your own request. Request creators automatically vote for their requests.');
      } else {
        alert('Failed to update vote. Please try again.');
      }
    }
  }, [user, onSignInNeeded, isPending, toggleOptimisticVote, clearPending]);

  // Initial load and refresh
  useEffect(() => {
    // Don't fetch until auth loading is complete
    if (authLoading) {
      console.log('RequestsList: Waiting for auth to complete before fetching');
      return;
    }
    
    console.log('RequestsList: useEffect triggered for fetchRequests', { 
      authLoading, 
      authenticated: !!user,
      sortBy, 
      searchQuery, 
      filterBy 
    });
    fetchRequests();
  }, [sortBy, searchQuery, filterBy, !!user, authLoading]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('RequestsList: Refresh trigger activated:', refreshTrigger);
      fetchRequests();
    }
  }, [refreshTrigger]);

  // Debug effect to monitor requests state changes
  useEffect(() => {
    console.log('RequestsList: Requests state updated, count:', requests.length);
    if (requests.length > 0) {
      console.log('RequestsList: First request sample:', requests[0]);
    }
  }, [requests]);

  if (loading) {
    console.log('RequestsList: Rendering loading state');
    return (
      <div className="flex justify-center items-center py-12">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-t-transparent border-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-12 h-12 border-4 border-t-transparent border-purple-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('RequestsList: Rendering error state:', error);
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to load requests</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchRequests}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 cursor-pointer transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    console.log('RequestsList: Rendering empty state');
    const getEmptyStateMessage = () => {
      if (filterBy === 'my-requests') {
        return "You haven't submitted any requests yet. Share your app ideas with the community!";
      } else if (filterBy === 'liked') {
        return "You haven't voted for any requests yet. Explore and support ideas you'd like to see built!";
      } else if (searchQuery) {
        return `No requests match "${searchQuery}". Try adjusting your search.`;
      } else {
        return 'Be the first to submit an app idea!';
      }
    };

    const getEmptyStateTitle = () => {
      if (filterBy === 'my-requests') {
        return 'No requests submitted';
      } else if (filterBy === 'liked') {
        return 'No voted requests';
      } else {
        return 'No requests found';
      }
    };

    return (
      <div className="text-center py-12">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{getEmptyStateTitle()}</h3>
          <p className="text-gray-600">{getEmptyStateMessage()}</p>
        </div>
      </div>
    );
  }

  const getResultsText = () => {
    const count = requests.length;
    const suffix = count !== 1 ? 's' : '';
    
    if (filterBy === 'my-requests') {
      return `${count} of your request${suffix}`;
    } else if (filterBy === 'liked') {
      return `${count} voted request${suffix}`;
    } else {
      return `${count} request${suffix} found`;
    }
  };

  console.log('RequestsList: Rendering', requests.length, 'requests');

  return (
    <div className="space-y-6">
      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {getResultsText()}
        </p>
        <button
          onClick={fetchRequests}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Requests Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {requests.map((request) => {
          if (!request || !request.id) {
            console.error('RequestsList: Invalid request object:', request);
            return null;
          }

          return (
            <RequestCard
              key={`request-${request.id}`}
              request={request}
              onClick={() => handleViewDetails(request)}
              onVoteClick={handleVoteToggle}
              currentUser={user}
              isVoting={isPending(request.id)}
              showVoteButton={true}
              showUserName={true}
            />
          );
        })}
      </div>
    </div>
  );
}